/**
 * Service for Authentication API calls
 * Communicates with backend authentication endpoints
 */

import { ENVIRONMENT } from '../config/properties';

// Error handling utility
const handleApiError = (error, context = '') => {
  console.error(`Auth Service Error (${context}):`, error);

  if (error instanceof SyntaxError) {
    throw new Error('Invalid response format from server. Response was not valid JSON.');
  }

  if (error.message && error.message.includes('Failed to fetch')) {
    throw new Error('Network error. Please check your connection and that the API server is running.');
  }

  throw error;
};

const authService = {
  /**
   * Login user with credentials
   * @param {string} username - Username or email
   * @param {string} password - Password
   * @returns {Promise<LoginResponseDto>} Login response with JWT token
   */
  login: async (username, password) => {
    try {
      const loginUrl = `${ENVIRONMENT.apiBaseUrl}/api/public/v1/auth/login`;
      console.log('Logging in user:', username);

      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: username,
          password: password,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`HTTP ${response.status}:`, errorText);
        if (response.status === 401) {
          throw new Error('Invalid username or password');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Login successful for user:', username);

      // Store token and user data
      if (data.accessToken) {
        localStorage.setItem('token', data.accessToken);
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('roles', JSON.stringify(data.roles));
        localStorage.setItem('sections', JSON.stringify(data.accessibleSections));
      }

      return data;
    } catch (error) {
      handleApiError(error, 'login');
    }
  },

  /**
   * Register a new user
   * @param {string} username - Username
   * @param {string} email - Email address
   * @param {string} password - Password
   * @param {string} firstName - First name
   * @param {string} lastName - Last name
   * @returns {Promise<UserDto>} Created user data
   */
  register: async (username, email, password, firstName, lastName) => {
    try {
      const registerUrl = `${ENVIRONMENT.apiBaseUrl}/api/public/v1/auth/register`;
      console.log('Registering new user:', username);

      const response = await fetch(registerUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: username,
          email: email,
          password: password,
          firstName: firstName,
          lastName: lastName,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`HTTP ${response.status}:`, errorText);
        throw new Error(`Registration failed: ${response.status}`);
      }

      const data = await response.json();
      console.log('Registration successful for user:', username);
      return data;
    } catch (error) {
      handleApiError(error, 'register');
    }
  },

  /**
   * Logout user (clear local storage)
   */
  logout: () => {
    console.log('Logging out user');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('roles');
    localStorage.removeItem('sections');
  },

  /**
   * Get stored JWT token
   * @returns {string|null} JWT token or null
   */
  getToken: () => {
    return localStorage.getItem('token');
  },

  /**
   * Get stored user data
   * @returns {Object|null} User object or null
   */
  getUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  /**
   * Get stored user roles
   * @returns {Array|null} Array of role names or null
   */
  getRoles: () => {
    const rolesStr = localStorage.getItem('roles');
    return rolesStr ? JSON.parse(rolesStr) : null;
  },

  /**
   * Get stored accessible sections
   * @returns {Array|null} Array of section objects or null
   */
  getSections: () => {
    const sectionsStr = localStorage.getItem('sections');
    return sectionsStr ? JSON.parse(sectionsStr) : null;
  },

  /**
   * Check if user is authenticated
   * @returns {boolean} True if token exists
   */
  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  /**
   * Check if user has specific role
   * @param {string} roleName - Role name to check
   * @returns {boolean} True if user has role
   */
  hasRole: (roleName) => {
    const roles = authService.getRoles();
    return roles ? roles.includes(roleName) : false;
  },

  /**
   * Check if user has ADMIN role
   * @returns {boolean} True if user is admin
   */
  isAdmin: () => {
    return authService.hasRole('ADMIN');
  },

  /**
   * Check if user can access section
   * @param {string} sectionName - Section name to check
   * @returns {boolean} True if user can access section
   */
  canAccessSection: (sectionName) => {
    if (authService.isAdmin()) {
      return true; // Admins have access to all sections
    }

    const sections = authService.getSections();
    return sections ? sections.some(s => s.name === sectionName) : false;
  },

  /**
   * Get Authorization header with Bearer token
   * @returns {Object} Headers object with Authorization
   */
  getAuthHeader: () => {
    const token = authService.getToken();
    if (token) {
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
    }
    return {
      'Content-Type': 'application/json',
    };
  },
};

export default authService;
